import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface SalesGoal {
  id: string;
  year: number;
  month: number;
  goal_amount: number;
  avg_ticket: number | null;
  currency: string;
  business_unit: string | null;
  created_at: string;
  updated_at: string;
}

export function useSalesGoals(year: number, month: number, businessUnit?: string | null) {
  const queryClient = useQueryClient();

  const { data: goal, isLoading, error } = useQuery({
    queryKey: ['sales-goals', year, month, businessUnit],
    queryFn: async () => {
      let query = supabase
        .from('sales_goals')
        .select('*')
        .eq('year', year)
        .eq('month', month);

      // Filter by business_unit - null means consolidated
      if (businessUnit) {
        query = query.eq('business_unit', businessUnit);
      } else {
        query = query.is('business_unit', null);
      }

      const { data, error } = await query.maybeSingle();

      if (error) throw error;
      return data as SalesGoal | null;
    },
  });

  const upsertGoal = useMutation({
    mutationFn: async ({ goalAmount, avgTicket }: { goalAmount?: number; avgTicket?: number }) => {
      const updateData: any = {
        year,
        month,
        business_unit: businessUnit || null,
        updated_at: new Date().toISOString(),
      };
      if (goalAmount !== undefined) updateData.goal_amount = goalAmount;
      if (avgTicket !== undefined) updateData.avg_ticket = avgTicket;

      const { data, error } = await supabase
        .from('sales_goals')
        .upsert(updateData, { onConflict: 'year,month,business_unit' })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-goals', year, month, businessUnit] });
      queryClient.invalidateQueries({ queryKey: ['sales-goals-projection'] });
    },
  });

  return {
    goal,
    goalAmount: goal?.goal_amount ?? 0,
    avgTicket: goal?.avg_ticket ?? 5800,
    isLoading,
    error,
    updateGoal: upsertGoal.mutate,
    isUpdating: upsertGoal.isPending,
  };
}

// Hook for consolidated goal (sum of US + BR)
export function useConsolidatedSalesGoal(year: number, month: number) {
  const { goalAmount: usGoal, isLoading: usLoading } = useSalesGoals(year, month, 'us');
  const { goalAmount: brGoal, isLoading: brLoading } = useSalesGoals(year, month, 'br');
  
  return {
    usGoal,
    brGoal,
    totalGoal: usGoal + brGoal,
    isLoading: usLoading || brLoading,
  };
}

// Hook for fetching sales goals for DRE projection
export function useSalesGoalsProjection(months: string[], businessUnit?: string) {
  return useQuery({
    queryKey: ['sales-goals-projection', months, businessUnit],
    queryFn: async () => {
      if (!months.length) return new Map<string, number>();

      // Parse months to get year-month pairs
      const yearMonthPairs = months.map(m => {
        const [year, month] = m.split('-');
        return { year: parseInt(year), month: parseInt(month), key: m };
      });

      const minYear = Math.min(...yearMonthPairs.map(p => p.year));
      const maxYear = Math.max(...yearMonthPairs.map(p => p.year));

      // Fetch all relevant sales_goals (always fetch both BUs for consolidated calculation)
      const { data: salesGoals, error: salesError } = await supabase
        .from('sales_goals')
        .select('*')
        .gte('year', minYear)
        .lte('year', maxYear);

      if (salesError) throw salesError;

      // Business unit filter
      const buFilter = businessUnit === 'usa' ? 'us' : businessUnit === 'brasil' ? 'br' : null;

      // Also fetch user_sales_goals (individual goals)
      let userQuery = supabase
        .from('user_sales_goals')
        .select('*')
        .gte('year', minYear)
        .lte('year', maxYear);

      if (buFilter) {
        userQuery = userQuery.or(`business_unit.eq.${buFilter},business_unit.is.null`);
      }

      const { data: userGoals, error: userError } = await userQuery;
      if (userError) throw userError;

      // Build a map of month -> goal amount
      const goalMap = new Map<string, number>();
      let lastKnownGoal = 0;

      yearMonthPairs.forEach(({ year, month, key }) => {
        let goalAmount = 0;

        if (buFilter) {
          // For specific BU, get that BU's goal
          const goal = salesGoals?.find(
            g => g.year === year && g.month === month && g.business_unit === buFilter
          );
          goalAmount = Number(goal?.goal_amount) || 0;
        } else {
          // For consolidated: SUM of US + BR goals (not null business_unit)
          const usGoal = salesGoals?.find(
            g => g.year === year && g.month === month && g.business_unit === 'us'
          );
          const brGoal = salesGoals?.find(
            g => g.year === year && g.month === month && g.business_unit === 'br'
          );
          goalAmount = (Number(usGoal?.goal_amount) || 0) + (Number(brGoal?.goal_amount) || 0);
        }

        if (goalAmount > 0) {
          goalMap.set(key, goalAmount);
          lastKnownGoal = goalAmount;
        } else {
          // Fallback: Sum user_sales_goals for this month
          const userGoalsForMonth = userGoals?.filter(
            g => g.year === year && g.month === month && 
            (buFilter ? g.business_unit === buFilter || g.business_unit === null : true)
          );

          if (userGoalsForMonth && userGoalsForMonth.length > 0) {
            const total = userGoalsForMonth.reduce((sum, g) => sum + (Number(g.goal_amount) || 0), 0);
            goalMap.set(key, total);
            lastKnownGoal = total;
          } else {
            // Carry forward last known goal
            goalMap.set(key, lastKnownGoal);
          }
        }
      });

      return goalMap;
    },
    enabled: months.length > 0,
  });
}
